import { Component, Fragment, lazy, Suspense } from 'react';
import _ from 'lodash';
import { WebApps } from '@ModelViews/ModelViews';
import { YFetch } from "@Utils/YFetch";
import { ServerPaths } from "@Utils/Paths";
import { IsNullOrWhiteSpace } from "@Utils/Micro";
import { DebugEx } from "@Utils/DebugEx";
import { LazyLoadingAnimation } from "@Primitive/Lazy";
import SingleBoard from "./SingleBoard";
import { BoardTheming } from "@UI/Dashboard/BoardTheming";
import { GetDefaultDashboardTheme } from "./Board";
import { ExDialog } from '@Primitive/ExDialog';
import { Props } from '@Utils/ReactProps';

const BoardManager = lazy(() => import('./BoardManager'));
// ================================================================================================
// Main App Layout
// ================================================================================================
interface MainAppLayoutProps extends Props<MainAppLayout> {
    width: number;
    height: number;
}
// ------------------------------------------------------------------------------------------------------
interface MainAppLayoutState {
    domainInfo: WebApps.DomainInfo;
    userInfo: WebApps.UserInfoView;
    userSettings: WebApps.UserSettings;
    ServersInfo: { [key: number]: WebApps.CyanServerInfo };
}
// ------------------------------------------------------------------------------------------------------
const defaultDashboardTheme = GetDefaultDashboardTheme();
// ------------------------------------------------------------------------------------------------------
export default class MainAppLayout extends Component<MainAppLayoutProps, MainAppLayoutState> {
    // ------------------------------------------------------
    constructor(props: MainAppLayoutProps) {
        super(props);

        // Init state
        this.state = {
            domainInfo: null,
            userInfo: null,
            userSettings: null,
            ServersInfo: null
        };
    }
    // ------------------------------------------------------
    componentDidMount() {
        // Start loading the information from server
        YFetch.request(ServerPaths["UserInfo"].Method, {
            url: ServerPaths["UserInfo"].Path,
            onSuccess: (dataIn: WebApps.UserInfoResp) => {
                if (dataIn.LoggedIn) {
                    if ((window as any).YodiDomain == null)
                        (window as any).YodiDomain = {};

                    // Update local domain info
                    _.forEach(dataIn.ServersInfo, s => {
                        if (!IsNullOrWhiteSpace(s.CyanURL) &&
                            !IsNullOrWhiteSpace(s.WebSocketURL)) {
                            if (!_.has((window as any).YodiDomain, s.Id)) {
                                (window as any).YodiDomain[s.Id] = {
                                    Cyan: s.CyanURL,
                                    WebSocket: s.WebSocketURL
                                };
                            }
                        }
                    });
                    var nodeKey = dataIn.DomainInfo?.PredefinedLoadingBoardInfo?.NodeKey;

                    // Parse settings
                    if (dataIn?.DomainInfo?.PredefinedLoadingBoardInfo?.BoardSettingsJSON) {
                        try {
                            var settings = JSON.parse(dataIn.DomainInfo.PredefinedLoadingBoardInfo.BoardSettingsJSON);
                            dataIn.DomainInfo.PredefinedLoadingBoardInfo.BoardSettings = settings;
                        } catch (ex) {
                            DebugEx.TraceException(ex);
                        }
                    }

                    // Set theme
                    if (dataIn?.DomainInfo?.PredefinedLoadingBoardInfo?.BoardThemeJSON) {
                        try {
                            var theme = JSON.parse(dataIn.DomainInfo.PredefinedLoadingBoardInfo.BoardThemeJSON);
                            let boardTheme = BoardTheming.GetInstance(nodeKey);
                            if (boardTheme == null) {
                                boardTheme = new BoardTheming(nodeKey, theme)
                            } else {
                                boardTheme.SetTheme(theme);
                            }
                        } catch (ex) {
                            DebugEx.TraceException(ex);
                        }
                    }

                    // Update UI
                    this.setState({
                        userInfo: dataIn.UserInfo,
                        userSettings: dataIn.Settings,
                        ServersInfo: dataIn.ServersInfo,
                        domainInfo: dataIn.DomainInfo
                    });

                    // Set the login info
                    DebugEx.LogLogin(dataIn.UserInfo.Email, dataIn.UserInfo.Domain);

                } else {
                    // Refresh the page
                    if (process.env.GLOW_BUILD)
                        location.reload();
                    else
                        location.replace("../login");
                }
            },
            onFail: (textStatus: string) => {
                DebugEx.TraceCritical("Failed to get user info", textStatus);
            }
        });
    }
    // --------------------------------------------------------------------------------------------------
    render() {
        // Handling loading animation
        if (this.state.userInfo == null)
            return <ExDialog Enable Conf={{
                body: <div style={{ color: defaultDashboardTheme.Loading.TextColor, padding: 50 }}>
                    <img
                        src={defaultDashboardTheme.Loading.LoadingImageURL} alt="Loading"
                        style={{ display: 'block', margin: 'auto', width: '200px' }} />
                    <div style={{ padding: '8px 0', margin: 20 }} className="loader">
                        <span></span>
                        <span></span>
                        <span></span>
                    </div>
                    {defaultDashboardTheme.Loading.NoPoweredByText == true ? null :
                        <h4 style={{ textAlign: 'center', fontWeight: 'bold' }}>Powered by Yodiwo</h4>}
                </div>
            }} />;

        if (this.state.domainInfo.IsOneBoardSolution)
            return <SingleBoard
                width={this.props.width}
                height={this.props.height}
                userInfo={this.state.userInfo}
                domainInfo={this.state.domainInfo}
                ServersInfo={this.state.ServersInfo}
                userSettings={this.state.userSettings}
            />;

        return <Fragment>
            <Suspense fallback={<LazyLoadingAnimation />} >
                <BoardManager
                    width={this.props.width}
                    height={this.props.height}
                    userInfo={this.state.userInfo}
                    domainInfo={this.state.domainInfo}
                    ServersInfo={this.state.ServersInfo}
                    userSettings={this.state.userSettings}
                />
            </Suspense>
        </Fragment>;
    }
    // --------------------------------------------------------------------------------------------------
}
// --------------------------------------------------------------------------------------------